import classNames from 'classnames/bind';
import { Form, Formik } from 'formik';
import React, { Fragment, useEffect, useState } from 'react';
import Dropzone from 'react-dropzone';

import Checkbox from '~/components/Form/Checkbox';
import DateTime from '~/components/Form/DateTime';
import TextField from '~/components/Form/TextField';
import Loading from '~/components/Loading';
import FormattedMessage from '~/components/common/FormattedMessage';
import SelectorButton, { Selection } from '~/components/ui/Button/Selector';

import { useAuth } from '~/modules/auth/useAuth';
import { addFlash } from '~/modules/flashes';
import { getFolders, uploadImage, fetchFolders } from '~/modules/gallery';

import { useAppDispatch, useAppSelector } from '~/hooks/redux';
import { FolderPicker, UPLOAD_MODES } from '~/views/Gallery/_components/Form';
import styles from '~/views/Gallery/_components/Form/ImageForm.scss';

const cx = classNames.bind(styles);

const UploadPreview = ({ file, folders, i, updateFile, removeFile }) => {
  const { isUploading, success, failure, title, tags, folder_id, nsfw } = file;

  const handleClick = (e) => {
    e.stopPropagation();
  };

  const handleRemove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    removeFile(i);
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.currentTarget;
    if (name === 'folder_id') updateFile(i, 'folder_name', null);
    if (name === 'folder_name') updateFile(i, 'folder_id', null);
    updateFile(i, name, type === 'checkbox' ? checked : value);
  };

  return (
    <div
      className={cx(
        'ImageForm__UploadPreview',
        'ImageForm__UploadPreview--big',
        {
          'ImageForm__UploadPreview--isUploading': isUploading,
          'ImageForm__UploadPreview--status__success': success,
          'ImageForm__UploadPreview--status__failure': failure,
        }
      )}
      key={`prev:${i}`}
      onClick={handleClick}
    >
      {isUploading && (
        <Loading className={cx('ImageForm__UploadPreviewLoader')} />
      )}
      <img src={file.data} alt={file.name} />
      <div>
        <TextField name="title" value={title} onChange={handleChange}>
          <FormattedMessage
            id="Gallery_UploadForm_Title"
            defaultMessage="Image Title (required)"
          />
        </TextField>
        <TextField name="tags" value={tags} onChange={handleChange}>
          <FormattedMessage
            id="Gallery_UploadForm_TagsShort"
            defaultMessage="Image Tags"
          />
        </TextField>
        <Checkbox name="nsfw" checked={nsfw} onChange={handleChange}>
          Adult
        </Checkbox>
      </div>
      <div>
        {/* @ts-expect-error */}
        <FolderPicker
          folders={folders}
          folder_id={folder_id}
          handleChange={handleChange}
          hideTitle
        />
      </div>
      <a
        className="ion-close-circled"
        onClick={handleRemove}
        title="Remove"
        href="javascript:void"
      />
    </div>
  );
};

const BulkUpload = () => {
  const dispatch = useAppDispatch();
  const { currentUser, isSignedIn, promptLogin } = useAuth();

  const folders = useAppSelector(getFolders);

  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState<File[]>([]);

  const setMode = (e, setFieldValue) =>
    setFieldValue('uploadMode', e.currentTarget.dataset.name);

  const updateFile = (index, name, value) => {
    setFiles((prevFiles) => {
      const updatedFiles = [...prevFiles];
      updatedFiles[index] = {
        ...updatedFiles[index],
        [name]: value,
      };
      return updatedFiles;
    });
  };

  const removeFile = (i) => {
    setFiles((prevFiles) => {
      const updatedFiles = [...prevFiles];
      updatedFiles.splice(i, 1);
      return updatedFiles;
    });
  };

  const handleUpload = (file, values) => {
    return dispatch(
      // @ts-expect-error
      uploadImage(
        {
          nsfw: file.nsfw === true,
          tags: file.tags ? file.tags.split(',') : [],
          folder_id: file.folder_id,
          folder_name: file.folder_name,
          files: [file],
          title: file.title || file.name,
          published_at:
            values.uploadMode === 'SCHEDULE' ? values.published_at : null,
        },
        {
          queue:
            values.uploadMode === 'QUEUE' || values.uploadMode === 'SCHEDULE',
        }
      )
    );
  };

  const uploadFile = (file, values) => {
    updateFile(file.index, 'isUploading', true);

    // @ts-expect-error
    handleUpload(file, values)
      .then(() => {
        updateFile(file.index, 'success', true);
        updateFile(file.index, 'isUploading', false);
      })
      .catch(() => {
        dispatch(addFlash('error', 'Upload failed, please try again later'));
      });
  };

  const handleDrop = (newFiles) => {
    setIsLoading(true);
    const processFiles = newFiles.map(
      (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);

          reader.onload = () => {
            resolve({
              title: file.name,
              name: file.name,
              size: file.size,
              type: file.type,
              data: reader.result,
            });
          };

          reader.onerror = reject;
        })
    );

    Promise.all(processFiles).then((values) => {
      setFiles([...files, ...values]);
      setIsLoading(false);
    });
  };

  const handleSubmit = (values) => {
    // Upload files one by one
    files
      // @ts-expect-error
      .filter((file) => !file.success)
      .forEach((file, i) =>
        uploadFile(
          {
            ...file,
            index: i,
          },
          values
        )
      );
  };

  useEffect(() => {
    promptLogin(() => {
      dispatch(fetchFolders(currentUser.username));
    })();
  }, []);

  if (!isSignedIn) return <Loading />;

  return (
    <div>
      <Formik
        initialValues={{
          name: '',
          tags: '',
          uploadMode: null,
        }}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form data-testid="form" style={{ maxWidth: '100%' }}>
            <Dropzone onDrop={handleDrop}>
              {({ getRootProps, getInputProps, isDragActive }) => (
                <div
                  {...getRootProps()}
                  className={cx('ImageForm__Dropzone', {
                    'ImageForm__Dropzone--active': isDragActive,
                  })}
                >
                  <h2>
                    <FormattedMessage id="Gallery_file_prompt" />
                  </h2>
                  <input {...getInputProps()} data-testid="fileinput" />
                  <div
                    className={cx(
                      'ImageForm__Uploads',
                      'ImageForm__Uploads--fullWidth'
                    )}
                  >
                    {files.map((file, i) => (
                      <UploadPreview
                        file={file}
                        folders={folders}
                        i={i}
                        removeFile={removeFile}
                        updateFile={updateFile}
                      />
                    ))}
                  </div>
                </div>
              )}
            </Dropzone>
            <SelectorButton
              type="submit"
              disabled={isLoading}
              // @ts-expect-error
              value={UPLOAD_MODES[values.uploadMode]}
            >
              <Selection
                data-name="PUBLISH"
                onClick={(e) => setMode(e, setFieldValue)}
              >
                Publish now
              </Selection>
              <Selection
                data-name="QUEUE"
                onClick={(e) => setMode(e, setFieldValue)}
              >
                Add to queue
              </Selection>
              <Selection
                data-name="SCHEDULE"
                onClick={(e) => setMode(e, setFieldValue)}
              >
                Publish at...
              </Selection>
            </SelectorButton>
            {values.uploadMode === 'SCHEDULE' && (
              <Fragment>
                <DateTime
                  // @ts-expect-error
                  value={values.published_at}
                  onChange={(value) => setFieldValue('published_at', value)}
                />
              </Fragment>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default BulkUpload;
